import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import proj4 from 'proj4';

// MUI
import { Checkbox, FormControlLabel } from '@mui/material';

// OpenLayers
import Geometry from 'ol/geom/Geometry';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
import OlBaseLayer from 'ol/layer/Base';
import OlFeature from 'ol/Feature';
import OlCollection from 'ol/Collection';
import OlPoint from 'ol/geom/Point';
import OlLineString from 'ol/geom/LineString';
import OlMultiLineString from 'ol/geom/MultiLineString';
import OlFormatWKT from 'ol/format/WKT';
import { Polygon as OlPolygon, MultiPolygon as OlMultiPolygon } from 'ol/geom';
import OlLayerVector from 'ol/layer/Vector';
import { DrawEvent } from 'ol/interaction/Draw';
import BaseEvent from 'ol/events/Event';
import OlLayerGroup from 'ol/layer/Group';
import OlSourceVector from 'ol/source/Vector';
import { register as OlRegister } from 'ol/proj/proj4';
import { get as OlGetProjection } from 'ol/proj';
import RenderFeature from 'ol/render/Feature';

// Custom components
import Map from '@/components/Map/Map';
import {
  Controls,
  CoordZoomStatusControl,
  FullScreenControl,
  RotateControl,
  ScaleLineControl,
  ScaleRatioControl,
  StatusControl,
  ZoomControl,
  ZoomToExtentControl,
  GeoLocateControl,
  RadiusRulerControl,
  ZoomSliderControl,
  CenterMarkerControl,
  ViewHistoryControl,
} from '@/components/Map/Controls';
import {
  SidebarContent,
  SidebarControl,
  SidebarPane,
  SidebarTabs,
  SidebarTabList,
  SidebarTabListItem,
  SidebarHeading,
} from '@/components/Map/Controls/Sidebar';
import { InfoPane, LayersPane, MeasuresPane, DataPane, CommentsPane, SearchPane } from '@/components/MapSidebarPanes';
import RoutePane, { RoutePoint } from '@/components/MapSidebarPanes/RoutePaneV2';
import { Layers, VectorLayer } from '@/components/Map/Layers';
import { Overlays, PopupOverlay, MeasureTooltipOverlay } from '@/components/Map/Overlays';
import { Interactions, DefaultInteractions, DrawInteraction } from '@/components/Map/Interactions';
import {
  measurementsStyle,
  measuringStyle,
  selectedStyle,
  commentsStyle,
  commentingStyle,
  highlightedPoiStyle,
  poiStyle,
  ktStyle,
} from '@/components/Map/mapStyles';
import UserContext from '@/components/UserContext/UserContext';
import GeoAPILayers from '@/components/Map/Layers/GEO/GeoAPILayers';
import ClientLayer from '@/components/Map/Layers/ClientLayer';
import GeoAPIBaseLayers from '@/components/Map/Layers/GEO/GeoAPIBaseLayers';
import useRouting from "@/components/MapRouting/useRouting";


// UI
import DialogContext from '@/ui/DialogContext/DialogContext';

// Views
import CommentInfoCard from '@/views/Komentari/CommentInfoCard';
import CommentDialog from '@/views/Komentari/CommentDialog';
import GSInfoCard from '@/views/GIS/MapPage/GSInfoCard';

// Lib
import { flattenLayers } from '@/lib/olHelpers';
import { Permissions } from '@/lib/permissions';
import dataController from '@/lib/dataController';

// Services
import mapService from '@/services/mapService';
import gsService from '@/services/gsService';
import api from '@/lib/api';
import { sifrarnici } from '@/lib/sifrarnici';

// Models
import modelComments from '@/models/komentari';
import modelCommentPhotos from '@/models/komentari_fotografije';
import modelZemljovidi from '@/models/zemljovidi';
import modelPOI from '@/models/poi';

// Types
import { DrawingType, CoordinateSystemType, BoolSettings, ViewOptionsType, IMapView } from '@/@types/common/index';
import { IMapProps, CardPosition, CardOverlayType, ICardOverlay, ICardOverlayData } from '@/@types/views/GIS/map';
import { GFIFeatureType } from '@/@types/services/gsService';
import { DCRecord } from '@/@types/lib/dataController';

const MainMap = (props: IMapProps) => {
  const userContext = useContext(UserContext);
  const dialogContext = useContext(DialogContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const { routingPoints, loadRoutingPoints, addToRoute } = useRouting({});

  const { mapId = 1 } = props;

  const [mapInitialized, setMapInitialized] = useState(false);

  const [recordsZemljovidi, setRecordsZemljovidi] = useState<DCRecord[]>([]);
  const [expandedPosjednici, setExpandedPosjednici] = useState(false);
  const [expandedVlasnici, setExpandedVlasnici] = useState(false);

  // localStorage map state
  const mapStateJSON = localStorage.getItem(`map_state_${userContext?.firstName.toLowerCase()}`);
  const [viewOptions, setViewOptions] = useState<ViewOptionsType>({
    center:
      mapStateJSON &&
        mapStateJSON !== null &&
        mapStateJSON !== undefined &&
        JSON.parse(mapStateJSON).center &&
        JSON.parse(mapStateJSON).center !== null
        ? JSON.parse(mapStateJSON).center
        : [1731757, 5581737],
    zoom:
      mapStateJSON &&
        mapStateJSON !== null &&
        mapStateJSON !== undefined &&
        JSON.parse(mapStateJSON).zoom &&
        JSON.parse(mapStateJSON).zoom !== null
        ? JSON.parse(mapStateJSON).zoom
        : 8,
    // extent: userContext?.mapSettings ? userContext.mapSettings.max_extent : undefined,
    extent: [1447153, 5104226, 2162937, 5867310],
    constrainOnlyCenter: true,
    // projection: wgs84PM,
    minZoom: 8,
    maxZoom: 18,
    constrainResolution: true,
  });

  const [drawingSource, setDrawingSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));
  const [measurementsSource, setMeasurementsSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [chosenCoordinateSystem, setChosenCoordinateSystem] = useState<CoordinateSystemType>('WGS84');
  const [layersCollection, setLayersCollection] = useState<OlCollection<OlBaseLayer> | undefined>(undefined);
  const [clientLayersCollection, setClientLayersCollection] = useState<OlCollection<OlBaseLayer>>(new OlCollection());
  const [baseLayersCollection, setBaseLayersCollection] = useState<Array<OlBaseLayer> | undefined>(undefined);
  const [forceRefreshCounter, setForceRefreshCounter] = useState(0);
  const [drawType, setDrawType] = useState<DrawingType | undefined>(undefined);
  const [isDrawingRoute, setIsDrawingRoute] = useState(false);
  const [feature, setFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [measuringFeature, setMeasuringFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [commentingFeature, setCommentingFeature] = useState<OlFeature<Geometry> | undefined>(undefined);
  const [cardOverlay, setCardOverlay] = useState<ICardOverlay | undefined>(undefined);
  const [defaultExtent, setDefaultExtent] = useState(
    userContext?.mapSettings?.default_extent ? userContext.mapSettings.default_extent : [1437016, 5271097, 2206278, 5860579]
  );
  const [zoomToExtent, setZoomToExtent] = useState(undefined);

  const [selectedSource, setSelectedSource] = useState<OlSourceVector<OlFeature<Geometry>>>(new OlSourceVector({}));

  const [commentRecords, setCommentRecords] = useState<DCRecord[]>([]);
  const [commentDrawType, setCommentDrawType] = useState<DrawingType | undefined>(undefined);
  const commentDrawId = useRef<string | undefined>(undefined);
  const [commentsVisible, setCommentsVisible] = useState<boolean>(true);

  const [commentsFilteredForObject, setCommmentsFilteredForObject] = useState<DCRecord[]>([]);

  const [poiSources, setPoiSources] = useState<Array<OlSourceVector<OlFeature<Geometry>>>>();

  const highlightedPOI = useRef<OlFeature<Geometry> | null>(null);
  const zoomToDefaultExtentElementRef = useRef<HTMLLIElement>(null);
  const fullScreenElementRef = useRef<HTMLLIElement>(null);
  const geoLocateElementRef = useRef<HTMLLIElement>(null);
  const radiusRulerElementRef = useRef<HTMLLIElement>(null);
  const centerMarkerElementRef = useRef<HTMLLIElement>(null);

  // define proj
  proj4.defs(
    'EPSG:3765',
    '+proj=tmerc +lat_0=0 +lon_0=16.5 +k=0.9999 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs'
  );
  proj4.defs('EPSG:4326', '+proj=longlat +datum=WGS84 +no_defs');
  proj4.defs(
    'EPSG:31276',
    '+proj=tmerc +pm=greenwich +lat_0=0 +lon_0=18 +k=0.9999 +x_0=6500000 +y_0=0 +ellps=bessel +towgs84=550.499,164.116,475.142,5.80967,2.07902,-11.62386,0.99999445824 +units=m +no_defs'
  );
  proj4.defs(
    'EPSG:3857',
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +wktext  +no_defs'
  );
  OlRegister(proj4);

  const htrs96 = OlGetProjection('EPSG:3765');
  const wgs84 = OlGetProjection('EPSG:4326');
  const wgs84PM = OlGetProjection('EPSG:3857');

  // defaultViewCenter = [1731757, 5581737];
  // initialDefaultExtent = [1688674,5501166,1688774,5501266];
  const defaultViewCenter = userContext?.mapSettings?.initial_view_center;
  const initialDefaultExtent = userContext?.mapSettings?.default_extent;

  const dcZemljovidi = new dataController(modelZemljovidi);
  const dcKomentari = new dataController(modelComments);
  const dcPOI = new dataController(modelPOI);

  useEffect(() => {
    getLayers();
    refreshPOI();
    checkForShare();
    checkForComment();
    getComments();
    if (userContext?.hasAnyPermission([Permissions.Administration, Permissions.HGSSMod, Permissions.HGSSRead])) {
      refreshZemljovidi();
    }
  }, []);

  const getLayers = () => {
    mapService.getLayers(mapId).then((coll) => {
      setLayersCollection(coll);
      setMapInitialized(true);
    });
  };

  const refreshZemljovidi = () => {
    dcZemljovidi.GetData().then((resp) => {
      if (resp.success) {
        setRecordsZemljovidi(resp.data as DCRecord[]);
      }
    });
  };

  const checkForShare = () => {
    const hash = new URLSearchParams(window.location.search).get('hash');
    const apiInstance = new api();

    if (hash) {
      apiInstance.Call(`share?hash=${hash}`, 'get').then((resp) => {
        if (resp.success && resp.data) {
          // @ts-ignore
          const data = JSON.parse(resp.data.config);
          localStorage.setItem('maplayers', data.maplayers);

          if(data.route_points) {
            localStorage.setItem("route_points", data.route_points);
            const rps = Array.isArray(data.route_points) ? data.route_points : JSON.parse(data.route_points);
            loadRoutingPoints(rps);
          }

          if (data.extent[0]) {
            setZoomToExtent(data.extent);
            // TODO highlight feature and open GSInfoCard
          }
        }
      });
    }
  };

  const checkForComment = () => {
    if (location.state) {
      // @ts-ignore
      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { komentar_guid } = location.state;

      if (komentar_guid) {
        const apiInstance = new api();
        apiInstance.Call(`komentari/get-by-guid?guid=${komentar_guid}`, 'get').then((resp) => {
          if (resp.success && resp.data) {
            // @ts-ignore
            const { id } = resp.data;

            if (id) {
              navigate(`/hgss/komentari?id=${id}`);
            }
          }
        });
      }
    }
  };

  // getDefaultData() {
  //   mapService.getDefaultData().then((data) => {
  //     if (data) {
  //       const viewData = Array.isArray(data) ? Object.assign({}, data[0]) : Object.assign({}, data);
  //       this.setState((prevState) => {
  //         return {
  //           ...prevState,
  //           viewOptions: {
  //             ...prevState.viewOptions,
  //             zoom: viewData.initial_view_zoom,
  //             center: viewData.initial_view_center
  //           },
  //           zoomToExtent: viewData.default_extent
  //         };
  //       });
  //     }
  //   });
  // }

  const setViewCenter = (view: IMapView) => {
    setViewOptions((prevState) => ({
      ...prevState,
      center: view.center,
      zoom: view.zoom,
    }));
  };

  const changeCoordinateSystemDisplay = (type: CoordinateSystemType) => {
    setChosenCoordinateSystem(type);
  };

  // For drawing comments
  const commentsSource = useMemo(() => {
    const features = commentRecords.map((x) => x.geom);
    // @ts-ignore
    const commentsSource = new OlSourceVector({
      // @ts-ignore
      features: features as OlFeature<Geometry>[],
    });
    return commentsSource;
  }, [commentRecords]);

  const createPOIFeatures = (records: DCRecord[]) =>
    records.map((x) => {
      // @ts-ignore
      const f = x.geom as OlFeature<Geometry>;
      f.setId(x.id as number);
      f.set('poi_tip_id', x.poi_tip_id);
      f.set('naziv', x.naziv);
      f.set('ttoken', x.ttoken);
      return f;
    });

  const createPOILayerVector = (id: string, token: string, source: OlSourceVector) => {
    const layer = new OlLayerVector({
      source,
      visible: true,
    });
    layer.set('id', id);
    layer.set('title', token);
    layer.set('icon', 'images/icons/dom_1.svg');
    return layer;
  };

  const refreshPOI = () => {
    dcPOI.GetData('public/poi').then((resp) => {
      if (resp.success) {
        if (Array.isArray(resp.data) && resp.data.length > 0) {
          const featuresPlaninarski = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id === 31 || x.poi_tip_id === 32))
          );
          const featuresSkloniste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 33));
          const featuresPenjaliste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 40));
          const featuresOpasnost = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 50));
          const featuresVrh = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 60));
          const featuresOdmoriste = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 91));
          const featuresPolazista = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 90));
          const featuresPrir = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 70 && (x.poi_tip_id as number) <= 73)
          );
          const featuresKult = createPOIFeatures(
            resp.data.filter((x) => x.geom !== undefined && (x.poi_tip_id as number) >= 81 && (x.poi_tip_id as number) <= 86)
          );
          const featuresHGSS = createPOIFeatures(resp.data.filter((x) => x.geom !== undefined && x.poi_tip_id === 20));
          const featuresObilaznicaKTs = resp.data
            .filter((x) => x.geom !== undefined && x.is_obilaznica_kt === true)
            .map((x) => {
              // @ts-ignore
              const f = (x.geom as OlFeature<Geometry>).clone();
              f.setId(x.id as number);
              return f;
            });

          const newPoiSources = [
            featuresPlaninarski,
            featuresSkloniste,
            featuresPenjaliste,
            featuresOpasnost,
            featuresVrh,
            featuresOdmoriste,
            featuresPolazista,
            featuresPrir,
            featuresKult,
            featuresHGSS,
            featuresObilaznicaKTs,
          ].map((x) => new OlSourceVector<OlFeature<Geometry>>({ features: x }));

          setPoiSources(newPoiSources);

          const layerPlanKuce = createPOILayerVector('poi-planinarski', 'map:layer.poi_plan_kuce_i_domovi', newPoiSources[0]);
          const layerPlanSklonista = createPOILayerVector('poi-sklonista', 'map:layer.poi_plan_sklonista', newPoiSources[1]);
          const layerPenjalista = createPOILayerVector('poi-penjalista', 'map:layer.poi_penjalista', newPoiSources[2]);
          const layerOpasnost = createPOILayerVector('poi-opasnost', 'map:layer.poi_opasnost', newPoiSources[3]);
          const layerVrhovi = createPOILayerVector('poi-vrh', 'map:layer.vrhovi', newPoiSources[4]);
          const layerOdmorista = createPOILayerVector('poi-odmorista', 'map:layer.odmorista', newPoiSources[5]);
          const layerPolazista = createPOILayerVector('poi-polazista', 'map:layer.polazista', newPoiSources[6]);
          const layerPrir = createPOILayerVector('poi-prir', 'map:layer.poi_prir', newPoiSources[7]);
          const layerKult = createPOILayerVector('poi-kult', 'map:layer.poi_kult', newPoiSources[8]);
          const layerHGSS = createPOILayerVector('poi-hgss', 'map:layer.poi_hgss', newPoiSources[9]);
          const layerObilaznicaKTs = createPOILayerVector('obilaznica-kts', 'map:layer.obilaznica_kts', newPoiSources[10]);

          const poiLayerGroup = new OlLayerGroup({
            layers: [
              layerPlanKuce,
              layerPlanSklonista,
              layerPenjalista,
              layerOpasnost,
              layerVrhovi,
              layerOdmorista,
              layerPolazista,
              layerPrir,
              layerKult,
              layerHGSS,
              layerObilaznicaKTs,
            ],
          });
          poiLayerGroup.set('id', 'poi');
          poiLayerGroup.set('title', 'map:layer.poi');

          setClientLayersCollection(new OlCollection([poiLayerGroup]));
        } else {
          // setPoiRecords([]);
        }
      }
    });
  };

  const getComments = () => {
    const dc = new dataController(modelComments);
    if (userContext?.hasAnyPermission([Permissions.CommentsRead]))
      dc.GetData()
        .then((resp) => {
          if (resp.success && resp.data) {
            if (Array.isArray(resp.data)) {
              // const features = resp.data.map((x) => x.geom);
              const tempComments: DCRecord[] = [];
              for (const x of resp.data) {
                if (x.hasOwnProperty('geom') && x.geom && x.geom !== null && !(x.objekt_type === 'POI')) {
                  tempComments.push(x);
                }
              }
              setCommentRecords(tempComments);
            } else {
              setCommentRecords([]);
            }
          } else {
            setCommentRecords([]);
          }
        })
        .catch((resp) => {
          setCommentRecords([]);
        });
  };

  const getPhotosForComment = (
    feature: RenderFeature | OlFeature<Geometry>,
    evt?: OlMapBrowserEvent<any>,
    coordinates?: [number, number]
  ) => {
    let photos: any = [];

    const dc = new dataController(modelCommentPhotos);

    dc.GetData(`komentari/${String(Number(feature.getId()))}/slike`)
      .then((resp) => {
        if (resp.success && resp.data && resp.data !== null) {
          if (Array.isArray(resp.data) && resp.data.length > 0) {
            // @ts-ignore L_TODO:
            photos = resp.data.slice();
            showCardOverlay(
              'commentinfo',
              {
                position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
                feature,
                record: photos,
                type: 'commentinfo',
              },
              null
            );
          } else {
            showCardOverlay(
              'commentinfo',
              {
                position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
                feature,
                type: 'commentinfo',
              },
              null
            );
          }
        }
      })
      .catch(() => {
        showCardOverlay(
          'commentinfo',
          {
            position: evt ? (evt.coordinate as CardPosition) : (coordinates as CardPosition),
            feature,
            type: 'commentinfo',
          },
          null
        );
      });
  };

  const handlePointerMove = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      if (highlightedPOI.current) {
        highlightedPOI.current.setStyle(poiStyle);
        highlightedPOI.current = null;
      }

      const zoom = evt.map.getView().getZoom();
      if (zoom && zoom <= 11) {
        return;
      }

      const feature = evt.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
        layerFilter: (layer) => {
          const layerId = layer.get('id');
          return layerId !== null && layerId !== undefined && layerId.startsWith('poi');
        },
        hitTolerance: 8,
      });

      if (feature && feature.getId() !== null && feature.getId() !== undefined) {
        const f = feature as OlFeature;
        const tip_id = f.get('poi_tip_id');
        if (tip_id != sifrarnici.poi_tipovi.opasnost) {
          highlightedPOI.current = f;
          highlightedPOI.current.setStyle(highlightedPoiStyle);
        }
        // } else if(feature?.get("staza_id") && feature?.get("staza_id") === "staza") {
        //   setHighlightedRoutePosition(evt.coordinate);
      }
    },
    [layersCollection]
  );

  const handleClick = useCallback(
    (evt: OlMapBrowserEvent<any>) => {
      let hit = false;

      if (drawType || isDrawingRoute) {
        return;
      }

      const feature = evt.map.forEachFeatureAtPixel(evt.pixel, (feature) => feature, {
        // layerFilter: (layer) => {
        //   const layerId = layer.get("id");
        //  return layerId !== null && layerId !== undefined && layerId.startsWith("objekti");
        // }
        hitTolerance: 8,
      });

      if (
        feature &&
        feature.getId() !== null &&
        feature.getId() !== undefined &&
        commentDrawType === undefined &&
        !feature.get('poi_tip_id')
      ) {
        getPhotosForComment(feature, evt);

        hit = true;
      } else if (feature && feature.getId() !== null && feature.getId() !== undefined && feature.get('poi_tip_id')) {
        const opasnost = false;
        // selectedCoords.current = [];
        hit = true;
        const id = feature.getId() as number;
        dcPOI
          .GetDataSingle(id)
          .then((resp) => {
            if (resp.data) {
              const rec = resp.data as DCRecord;
              showCardOverlay(
                'gsinfo',
                { feature, position: evt.coordinate as CardPosition, type: 'gsinfo', record: rec, layerId: 'poi_' },
                null
              );
            }
          })
          .then(() => {
            if (opasnost === false) {
              // setHighlightedRoutePosition([]);
              const f = feature as OlFeature<Geometry>;
              const selectedFeature = f.clone();
              const fid = f.getId();
              // selecetedFeature.setId(fid);
              selectedFeature.set('poi_id', fid);
              selectedFeature.setStyle(undefined);
              setSelectedSource(new OlSourceVector({ features: [selectedFeature] }));
            }
          });

        // } else if(feature && feature.get('poi_id') !== undefined) {
        //   selectedCoords.current = [];
        //   hit = true;
        //   setHighlightedRoutePosition([]);
        //   // const id = feature.get('poi_id') as number;
        //   // dcPOI.GetDataSingle(id).then((resp) => {
        //   //   if (resp.data) {
        //   //     const rec = resp.data as DCRecord;
        //   //     showInfoPane("poi", rec.poi_tip_id as number, { type: "poi", feature: feature, record: rec, layerId: "poi", coordinates: clickCoordinates, }, null);
        //   //   }
        //   // });
        //   setSelectedSource(new OlSourceVector({}))
        //   setInfoPane(undefined);
      }

      if (!hit) {
        const item = localStorage.getItem('maplayers');
        const visibility = item ? (JSON.parse(item) as BoolSettings) : ({} as BoolSettings);
        const hiddenLayers = visibility ? Object.keys(visibility).filter((key) => visibility[key] === false) : [];
        const allLayers = layersCollection ? flattenLayers(layersCollection.getArray()).filter((x) => !(x instanceof OlLayerGroup)) : [];
        const visibleLayers = allLayers.filter((x) => hiddenLayers.indexOf(x.get('id')) === -1);
        const GSLayerNames = visibleLayers.filter((x) => x.get('query') === true).map((x) => x.get('layer'));

        gsService.getFeatureInfo(evt.map, evt.pixel, GSLayerNames).then((resp) => {
          if (resp && Object.keys(resp).length !== 0) {
            const layerKeys = Object.keys(resp);
            const poiKeys = layerKeys.filter((k) => k.slice(0, 4) === 'poi_');
            const stazeKeys = layerKeys.filter((k) => k.slice(0, 6) === 'staze_');
            const lKey = poiKeys.length > 0 ? poiKeys[0] : stazeKeys.length > 0 ? stazeKeys[0] : layerKeys[0];
            const firstLayer = resp[lKey];
            const firstFeature = firstLayer[0];
            const featureType = 'generic';
            const poiLayers: GFIFeatureType[] = poiKeys.reduce((acc: GFIFeatureType[], curr: string) => {
              acc.push(...resp[curr]);
              return acc;
            }, []);
            const zemljovidiLayer = resp.zemljovidi;
            const zemljovidiFeature = zemljovidiLayer ? zemljovidiLayer[0] : undefined;

            if (poiLayers.length > 1) {
              const currentView = evt.map.getView();
              const currentZoom = currentView.getZoom();
              if (firstFeature && currentZoom !== undefined && currentZoom < 20) {
                if (firstFeature.geometry.coordinates.length === 2) {
                  currentView.animate({
                    center: firstFeature.geometry.coordinates as [number, number],
                    zoom: currentZoom + 2,
                  });
                  // if we manage to zoom in dont raise overlay
                  return;
                }
              }
            }

            const fId = Number.isNaN(parseInt(firstFeature.id, 10)) ? firstFeature.id : parseInt(firstFeature.id, 10);
            const data = {
              position: evt.coordinate as CardPosition,
              feature: firstFeature,
              type: featureType as CardOverlayType,
              record: {
                id: fId,
                ...firstFeature.properties,
                hgss_zemljovid: zemljovidiFeature?.properties?.id,
              },
              layerId: lKey,
            } as ICardOverlayData;
            // TODO: depending on ID, define data type for card as either GSInfo or CommentInfo

            if (commentDrawType !== undefined) {
              commentDrawId.current = String(data.record?.id);
            } else if (lKey === 'zp_sr' || lKey === 'zp_pp' || lKey === 'zp_np') {
              return;
            } else {
              showCardOverlay('gsinfo', data, null);
            }

            const olFeat = new OlFeature();
            switch (firstFeature.geometry.type) {
              case 'Point':
                olFeat.setGeometry(new OlPoint(firstFeature.geometry.coordinates as number[]));
                break;
              case 'LineString':
                olFeat.setGeometry(new OlLineString(firstFeature.geometry.coordinates as number[][]));
                break;
              case 'MultiLineString':
                olFeat.setGeometry(new OlMultiLineString(firstFeature.geometry.coordinates as number[][][]));
                break;
              case 'MultiPolygon':
                olFeat.setGeometry(new OlMultiPolygon(firstFeature.geometry.coordinates as number[][][][]));
                break;
              case 'Polygon':
                olFeat.setGeometry(new OlPolygon(firstFeature.geometry.coordinates as number[][][]));
                break;
              default:
                break;
            }
            setSelectedSource(new OlSourceVector({ features: [olFeat] }));

            // const showCard = () => showCardOverlay("gsinfo", data, null);
            // closeOverlays(showCard);
          } else {
            setSelectedSource(new OlSourceVector({}));
            closeOverlays();
          }
        });
      }
    },
    [drawType, isDrawingRoute, layersCollection, commentDrawType]
  );

  const showCardOverlay = (type: CardOverlayType, data: ICardOverlayData, ft: OlFeature<any> | null) => {
    // TODO: refactor
    feature ? feature.setProperties({ selected: false }) : null;

    if (ft) {
      ft.setProperties({ selected: true });
    }

    if (type === 'gsinfo') {
      setCardOverlay({
        type,
        data,
      });
      if (ft) {
        setFeature(ft);
      } else {
        setFeature(undefined);
      }
    } else if (type === 'commentinfo') {
      setCardOverlay({
        type,
        data,
      });
      if (ft) {
        setFeature(ft);
      } else {
        setFeature(undefined);
      }
    }
  };

  useEffect(() => {
    if (cardOverlay) {
      const objId = cardOverlay?.data.record?.id;
      const filteredRecords = commentRecords.filter((x) => x.objekt_id === objId);

      setCommmentsFilteredForObject(filteredRecords);
    }
  }, [cardOverlay, commentRecords]);

  const closeOverlays = (callback = () => { }) => {
    setCardOverlay(undefined);
    setSelectedSource(new OlSourceVector({}));
    setExpandedPosjednici(false);
    setExpandedVlasnici(false);
    // this.setState(
    //   {
    //     cardOverlay: undefined
    //   },
    //   typeof callback === "function" ? () => callback() : () => {}
    // );
  };

  const updateCardOverlay = (record: DCRecord) => {
    setCardOverlay((oldCardOverlay) => {
      if (oldCardOverlay === undefined) return oldCardOverlay;
      const { data, type } = oldCardOverlay;
      return {
        data: { ...data, record },
        type,
      };
    });
  };

  // const handleViewChangeCenter = (evt: MapEvent) => {
  //   if (evt && evt.map) {
  //     setViewOptions(prevState => {
  //       return {
  //         ...prevState,
  //         center: evt.map.getView().getCenter(),
  //         zoom: evt.map.getView().getZoom()
  //       } as ViewOptionsType
  //     })
  //   }
  // };

  const handleSidebarPaneChange = useCallback(
    (/* id */) => {
      if (drawType) {
        setDrawType(undefined);
      }
    },
    [drawType]
  );

  const handleAddToRouteFromCard = useCallback((coord: number[] | undefined, label: string, poi_id?: number) => {
    closeOverlays();
    addToRoute(coord, label, poi_id);
  }, [ addToRoute ]);

  const changeDrawType = useCallback(
    (type: DrawingType) => {
      if (drawType !== type) {
        setDrawType(type);
        setMeasuringFeature(undefined);
      } else {
        setDrawType(undefined);
        setMeasuringFeature(undefined);
      }
    },
    [drawType]
  );

  const handleDrawMeasureStart = (evt: DrawEvent) => {
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setMeasuringFeature(evt.feature);
  };

  const handleDrawMeasureEnd = (evt: DrawEvent) => {
    setDrawingSource(new OlSourceVector({}));
    setMeasurementsSource(
      (prevState) =>
        new OlSourceVector({
          features: [...prevState.getFeatures(), evt.feature],
        })
    );

    setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
  };

  const handleDrawMeasureChange = (evt: BaseEvent) => {
    // console.log("handleDrawChange", evt);
  };

  const handleEraseMeasurements = () => {
    setMeasurementsSource(new OlSourceVector({}));
  };

  const handleDrawCommentsStart = (evt: DrawEvent) => {
    if (drawingSource.getFeatures().length > 0) {
      setDrawingSource(new OlSourceVector({}));
    }

    setCommentingFeature(evt.feature);
  };

  const handleDrawCommentsEnd = (evt: DrawEvent) => {
    setTimeout(() => {
      const newFeature = evt.feature;
      setDrawingSource(new OlSourceVector({}));

      const wktFormatter = new OlFormatWKT();
      const wkt = wktFormatter.writeFeature(newFeature, {
        dataProjection: 'EPSG:3857',
        featureProjection: 'EPSG:3857',
      });

      const dialogData: any = {
        dc: dcKomentari,
        form: 'insert',
        mode: 'insert',
        initialRecord: {
          wkt,
        },
        refreshLayers: getLayers,
        onClose: () => {
          setCommentDrawType(undefined);
          setCommentingFeature(undefined);
          getComments();
        },
      };

      commentDrawId.current = undefined;
      dialogContext.showDialog(CommentDialog, dialogData);
      setForceRefreshCounter((prevState) => (prevState ? prevState + 1 : 1));
    }, 1000);
  };

  const handleDrawCommentsChange = (evt: BaseEvent) => {
    // console.log("handleDrawChange", evt);
  };

  const changeCommentDrawType = useCallback(
    (type: DrawingType) => {
      if (commentDrawType !== type) {
        setCommentDrawType(type);
        setCommentingFeature(undefined);
      } else {
        setCommentDrawType(undefined);
        setCommentingFeature(undefined);
      }
    },
    [commentDrawType]
  );

  const handleLayerVisibilityChange = () => {
    // not needed as refreshCounter is called through MapContext
    // setForceRefreshCounter((prevState) => prevState + 1);
  };

  const gsOverlay = cardOverlay && cardOverlay.type === 'gsinfo' ? cardOverlay : undefined;

  return userContext && layersCollection ? (
    <Map
      mapId={mapId}
      height="800px"
      view={viewOptions}
      onClick={handleClick}
      onPointermove={handlePointerMove}
      // onMoveend={handleViewChangeCenter}
      className="sidebar-map"
      id="main-map"
      initialized={mapInitialized}
      moveTolerance={10}
      maxTilesLoading={10}
      zoomToExtent={zoomToExtent}
    >
      <Controls>

        <SidebarControl>
          <SidebarTabs>
            <SidebarTabList>
              <SidebarTabListItem id="info" title={t('map:sidebar.info')} icon={<i className="fas fa-info-circle" />} />
              <SidebarTabListItem id="layers" title={t('map:sidebar.layers')} icon={<i className="fas fa-layer-group" />} />
              <SidebarTabListItem id="search" title={t('map:sidebar.search')} icon={<i className="fas fa-search" />} />
              <SidebarTabListItem
                id="zemljovidi"
                needPermission={[Permissions.HGSSRead]}
                title={t('titles.zemljovidi')}
                icon={<i className="fas fa-map" />}
              />
              <SidebarTabListItem
                id="route"
                title={t('map:sidebar.route')}
                icon={<i className="fas fa-map-signs" />}
                badgeContent={routingPoints.length}
              />
              <SidebarTabListItem id="measures" title={t('map:sidebar.measures')} icon={<i className="fas fa-pencil-ruler" />} />
              <SidebarTabListItem id="komentari" title={t('map:sidebar.komentari')} icon={<i className="fas fa-comment" />} />
              <li ref={zoomToDefaultExtentElementRef} />
              <li ref={fullScreenElementRef} />
              <li ref={geoLocateElementRef} />
              <li id="center-marker-container" ref={centerMarkerElementRef} />
              <li ref={radiusRulerElementRef} />
            </SidebarTabList>
          </SidebarTabs>
          <SidebarContent>
            <SidebarHeading/>
            <SidebarPane id="info">
              <InfoPane />
            </SidebarPane>
            <SidebarPane id="layers">
              <LayersPane
                mapId={mapId}
                ready={!!layersCollection}
                baseLayers={baseLayersCollection || []}
                apiLayersCollection={layersCollection}
                clientLayersCollection={clientLayersCollection}
                onLayerVisibilityChange={handleLayerVisibilityChange}
              />
            </SidebarPane>
            <SidebarPane id="search">
              <SearchPane onAddToRoute={addToRoute} /> 
            </SidebarPane>
            <SidebarPane id="zemljovidi" needPermission={[Permissions.HGSSRead]}>
              <DataPane dc={dcZemljovidi} tableName="zemljovidi" viewName="sidebar" records={recordsZemljovidi} />
            </SidebarPane>
            <SidebarPane id="route">
              <RoutePane />
            </SidebarPane>
            <SidebarPane id="measures">
              <MeasuresPane
                // toggleDraw={handleToggleDrawMeasure} TODO: implement method handleToggleDrawMeasure in this component
                changeDrawType={changeDrawType}
                handleEraseMeasurements={handleEraseMeasurements}
                drawType={drawType}
              />
            </SidebarPane>
            <SidebarPane id="komentari">
              <FormControlLabel
                control={
                  <Checkbox
                    defaultChecked
                    color="primary"
                    onChange={() => {
                      setCommentsVisible((prevState) => !prevState);
                    }}
                  />
                }
                label={t('komentari.view')}
              />
              <CommentsPane
                // toggleDraw={handleToggleDrawMeasure} TODO: implement method handleToggleDrawMeasure in this component
                changeDrawType={changeCommentDrawType} // changeCommentDrawType
                drawType={commentDrawType} // commentDrawType
                records={commentRecords} // commentRecords
              />
            </SidebarPane>
          </SidebarContent>
        </SidebarControl>
        {/* <AttributionControl/> */}
        {/* <OverviewMapControl /> */}

        <CoordZoomStatusControl chosenCoordinateSystem={chosenCoordinateSystem} />
        <StatusControl changeCoordinateSystem={changeCoordinateSystemDisplay} />
        <ZoomControl zoomInTipLabel={t('map:controls.zoom_in')} zoomOutTipLabel={t('map:controls.zoom_out')} />
        <ZoomSliderControl />
        <ScaleRatioControl ppi={96} />
        <ScaleLineControl />
        {/* <FullScreenControl id="full-screen-ctrl" tipLabel={t("map:controls.full_screen")} /> */}
        <RotateControl autoHide={false} tipLabel={t('map:controls.reset_north')} />
        <ViewHistoryControl />
        {fullScreenElementRef?.current && mapInitialized ? (
          <FullScreenControl
            id="full-screen"
            tipLabel={t('map:controls.full_screen')}
            target={fullScreenElementRef?.current}
            className="ol-sidebar-control"
          />
        ) : null}

        {zoomToDefaultExtentElementRef?.current && mapInitialized ? (
          <ZoomToExtentControl
            id="zoom-extent-default"
            target={zoomToDefaultExtentElementRef.current}
            extent={defaultExtent}
            tipLabel={t('map:controls.zoom_to_extent')}
            className="ol-sidebar-control"
          />
        ) : null}
        {geoLocateElementRef?.current && mapInitialized ? (
          <GeoLocateControl
            id="geo-locate"
            tooltip={t('map:controls.geolocate')}
            target={geoLocateElementRef.current}
            className="ol-sidebar-control"
          />
        ) : null}
        {centerMarkerElementRef?.current && mapInitialized ? (
          <CenterMarkerControl
            id="center-marker"
            tooltip={t('map:controls.center_marker')}
            target={centerMarkerElementRef.current}
            className="ol-sidebar-control"
          />
        ) : null}
        {radiusRulerElementRef?.current && mapInitialized ? (
          <RadiusRulerControl
            id="radius-ruler"
            tooltip={t('map:controls.radius_ruler')}
            target={radiusRulerElementRef.current}
            className="ol-sidebar-control"
          />
        ) : null}
      </Controls>
      <Layers>
        <GeoAPIBaseLayers setBaseLayersCollection={setBaseLayersCollection} mapId={mapId} />
        {layersCollection ? (
          <Layers>
            <ClientLayer
              id="poi-planinarski"
              source={poiSources ? poiSources[0] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={708}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-sklonista"
              source={poiSources ? poiSources[1] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={707}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-penjalista"
              source={poiSources ? poiSources[2] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={706}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-opasnost"
              source={poiSources ? poiSources[3] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={705}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-vrh"
              source={poiSources ? poiSources[4] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={704}
              style={poiStyle}
              maxResolution={40}
            />
            <ClientLayer
              id="poi-odmorista"
              source={poiSources ? poiSources[5] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={702}
              style={poiStyle}
            />
            <ClientLayer
              id="poi-polazista"
              source={poiSources ? poiSources[6] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={701}
              style={poiStyle}
            />
            <ClientLayer id="poi-prir" source={poiSources ? poiSources[7] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={699} style={poiStyle} />
            <ClientLayer id="poi-kult" source={poiSources ? poiSources[8] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={698} style={poiStyle} />
            <ClientLayer id="poi-hgss" source={poiSources ? poiSources[9] : new OlSourceVector<OlFeature<Geometry>>()} zIndex={700} style={poiStyle} />
            <ClientLayer
              id="obilaznica-kts"
              source={poiSources ? poiSources[10] : new OlSourceVector<OlFeature<Geometry>>()}
              zIndex={710}
              style={ktStyle}
            />
            <GeoAPILayers layersCollection={layersCollection} isPrivate />
            <VectorLayer id="selected" source={selectedSource} style={selectedStyle} zIndex={800} />
            <VectorLayer id="measurements" source={measurementsSource} style={measurementsStyle} zIndex={900} />
            <VectorLayer id="spatial-comments" source={commentsSource} style={commentsVisible ? commentsStyle : null} zIndex={905} />
          </Layers>
        ) : null}
      </Layers>
      <Overlays>
        <PopupOverlay id="feature-overlay" position={gsOverlay ? gsOverlay.data.position : undefined} autoPan onClose={() => { }}>
          {gsOverlay ? (
            <GSInfoCard
              featureData={gsOverlay.data}
              onClose={closeOverlays}
              updateCardOverlay={updateCardOverlay}
              expandedVlasnici={expandedVlasnici}
              setExpandedVlasnici={setExpandedVlasnici}
              expandedPosjednici={expandedPosjednici}
              setExpandedPosjednici={setExpandedPosjednici}
              refreshLayers={getComments}
              commentRecords={commentsFilteredForObject}
              onAddToRoute={handleAddToRouteFromCard}
            />
          ) : null}
        </PopupOverlay>
        <PopupOverlay id="comment-overlay" position={cardOverlay ? cardOverlay.data.position : undefined} autoPan onClose={() => { }}>
          {cardOverlay && cardOverlay.type === 'commentinfo' ? (
            <CommentInfoCard
              featureData={cardOverlay.data}
              onClose={closeOverlays}
              onDelete={getComments}
              refreshLayers={getComments}
              refreshPhotos={getPhotosForComment}
            />
          ) : null}
        </PopupOverlay>
        <MeasureTooltipOverlay id="measure-overlay" feature={measuringFeature} position={undefined} />
      </Overlays>
      <Interactions>
        <DefaultInteractions />
        {drawType ? (
          <DrawInteraction
            source={drawingSource}
            type={drawType}
            style={measuringStyle}
            onChange={handleDrawMeasureChange}
            onDrawstart={handleDrawMeasureStart}
            onDrawend={handleDrawMeasureEnd}
          />
        ) : null}
        {commentDrawType ? (
          <DrawInteraction
            source={drawingSource}
            type={commentDrawType}
            style={commentingStyle}
            onChange={handleDrawCommentsChange}
            onDrawstart={handleDrawCommentsStart}
            onDrawend={handleDrawCommentsEnd}
          />
        ) : null}
      </Interactions>
    </Map>
  ) : null;
};

export default MainMap;
